import { ActionItemsFilterFormValue, BusinessUnit, ProjectFilters, PssrListFilterFormValue } from '#models';
import { computed, inject, Injectable, signal } from '@angular/core';
import { STORAGE_TOKEN } from './storage.token';
import { CategoryFilter } from 'src/models/dtos/category-filter';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  // Dependency Injection
  private _storage = inject(STORAGE_TOKEN);

  // Private Signals
  private _storageInfo = signal<Record<string, unknown>>({});
  private readonly _keys = [
    'pssrFilters',
    'projectFilters',
    'localActionItemsFilter',
    'localCategoryFilter',
    'selectedBU',
  ];

  // Computed Values
  pssrFilters = computed(() => {
    const { pssrFilters = null } = this._storageInfo();
    return pssrFilters as PssrListFilterFormValue | null;
  });

  projectFilters = computed(() => {
    const { projectFilters = null } = this._storageInfo();
    return projectFilters as ProjectFilters | null;
  });

  actionItemsFilter = computed(() => {
    const { localActionItemsFilter = null } = this._storageInfo();
    return localActionItemsFilter as ActionItemsFilterFormValue | null;
  });

  categoryFilters = computed(() => {
    const { localCategoryFilter = null } = this._storageInfo();
    return localCategoryFilter as CategoryFilter | null;
  });

  selectedBusinessUnit = computed(() => {
    const { selectedBU = null } = this._storageInfo() as Record<string, BusinessUnit>;
    return selectedBU as BusinessUnit | null;
  });

  // Constructor
  constructor() {
    this.initializeStorage();
  }

  // Public Methods
  setPssrFilters(rawFilter: PssrListFilterFormValue) {
    this.saveEntry<PssrListFilterFormValue>('pssrFilters', rawFilter);
  }

  removePssrFilters() {
    this.deleteEntry('pssrFilters');
  }

  setProjectFilters(rawFilter: ProjectFilters) {
    this.saveEntry<ProjectFilters>('projectFilters', rawFilter);
  }

  removeProjectFilters() {
    this.deleteEntry('projectFilters');
  }

  setActionItemsFilter(rawFilters: ActionItemsFilterFormValue) {
    this.saveEntry<ActionItemsFilterFormValue>('localActionItemsFilter', rawFilters);
  }

  removeActionItemsFilter() {
    this.deleteEntry('localActionItemsFilter');
  }

  setCategoryFilters(rawFilter: CategoryFilter) {
    this.saveEntry<CategoryFilter>('localCategoryFilter', rawFilter);
  }

  removeCategoryFilters() {
    this.deleteEntry('localCategoryFilter');
  }

  setBusinessUnit(rawBusinessUnit: BusinessUnit) {
    this.saveEntry<BusinessUnit>('selectedBU', rawBusinessUnit);
  }

  // Private Methods
  private initializeStorage() {
    try {
      this._keys.forEach(key => {
        this._storageInfo.update(storage => ({
          ...storage,
          [key]: JSON.parse(this._storage.getItem(key) || 'null'),
        }));
      });
    } catch (error) {
      console.error(error);
    }
  }

  private saveEntry<T>(key: string, rawJson: T) {
    this._storage.setItem(key, JSON.stringify(rawJson));
    this._storageInfo.update(storage => ({ ...storage, [key]: rawJson }));
  }

  private deleteEntry(key: string) {
    this._storage.removeItem(key);
    this._storageInfo.update(storage => ({ ...storage, [key]: undefined }));
  }
}
